import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { DEFAULT_PARTNER_FORM_OPTIONS } from '../Constants/partnerFormOptions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { sortMasterDataValues } from '../Utils/projectMasterDataUtils';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

const buildSortedFormOptions = (data = DEFAULT_PARTNER_FORM_OPTIONS) => ({
  productsInScope: sortMasterDataValues(
    data.productsInScope || DEFAULT_PARTNER_FORM_OPTIONS.productsInScope,
  ),
  engagementTypes: sortMasterDataValues(
    data.engagementTypes || DEFAULT_PARTNER_FORM_OPTIONS.engagementTypes,
  ),
  deploymentModels: sortMasterDataValues(
    data.deploymentModels || DEFAULT_PARTNER_FORM_OPTIONS.deploymentModels,
  ),
  projectStatus: sortMasterDataValues(
    data.projectStatus || DEFAULT_PARTNER_FORM_OPTIONS.projectStatus,
  ),
  competitiveContexts: sortMasterDataValues(
    data.competitiveContexts || DEFAULT_PARTNER_FORM_OPTIONS.competitiveContexts,
  ),
});

export const usePartnerFormOptions = () => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [formOptions, setFormOptions] = useState(() => buildSortedFormOptions());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedOnceRef = useRef(false);

  const loadFormOptions = useCallback(async () => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      setFormOptions(buildSortedFormOptions());
      setLoading(false);
      hasLoadedOnceRef.current = true;
      return;
    }

    // Only show the loading gate on the first fetch so remounts do not wipe typed form data.
    if (!hasLoadedOnceRef.current) {
      setLoading(true);
    }
    setError(null);

    const fetchOptions = async (activeToken) => {
      const data = await partnerPortalApi.fetchFormOptions(activeToken);
      setFormOptions(buildSortedFormOptions(data));
    };

    try {
      await fetchOptions(token);
    } catch (err) {
      if (isUnauthorizedError(err) && Cookies.get('refreshToken')) {
        try {
          await dispatch(refreshToken());
          const refreshedToken = Cookies.get('accessToken');
          if (refreshedToken) {
            await fetchOptions(refreshedToken);
            return;
          }
        } catch (refreshError) {
          setError(refreshError.message || err.message);
          setFormOptions(buildSortedFormOptions());
          return;
        }
      }

      setError(err.message);
      setFormOptions(buildSortedFormOptions());
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  return {
    formOptions,
    loading,
    error,
    refreshFormOptions: loadFormOptions,
  };
};
